'use client';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ChevronRight } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { cn } from '../../lib/utils.js';
import { AgendaItem } from './agenda-item';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/loading-spinner';
import { RichTextDisplay } from '../ui/rich-text-display';
// `pdf-viewer` statically imports `react-pdf`/`pdfjs-dist`, which reference
// browser-only globals (e.g. `DOMMatrix`) at module-evaluation time. Loading it
// via `next/dynamic({ ssr: false })` keeps it out of the server bundle and avoids
// "DOMMatrix is not defined" crashes during server rendering.
var PdfViewer = dynamic(function () { return import('../ui-custom/pdf-viewer').then(function (mod) { return ({ default: mod.PdfViewer }); }); }, {
    ssr: false,
    loading: function () { return (<div className="flex h-full items-center justify-center">
        <LoadingSpinner size={40}/>
      </div>); },
});
function AgendaSectionComponent(_a) {
    var onAttachmentClick = _a.onAttachmentClick, paddingLevel = _a.paddingLevel, renderItem = _a.renderItem, renderSection = _a.renderSection, section = _a.section, sectionNumber = _a.sectionNumber;
    var sectionItems = section.legislativeItems || [];
    var childSections = section.childSections || [];
    var displayChildSectionsFirst = section.displayChildSectionsFirst;
    // Top-level sections sit under the page's "Meeting Agenda" h2; each nesting level goes one deeper.
    var sectionHeadingLevel = Math.min(3 + paddingLevel, 5);
    var itemHeadingLevel = (sectionHeadingLevel + 1);
    var SectionHeading = "h".concat(sectionHeadingLevel);
    // If custom render function provided, use it for section header
    if (renderSection) {
        var customChildSectionsNode = childSections.length > 0 && (<div className={cn('space-y-6', displayChildSectionsFirst ? 'mb-4' : 'mt-4')}>
        {childSections.map(function (childSection, childIdx) {
                var childSectionNumber = childSection.sectionNumber || "".concat(sectionNumber.replace(/\.$/, ''), ".").concat(childIdx + 1, ".");
                return (<AgendaSectionComponent key={childSection.id} onAttachmentClick={onAttachmentClick} paddingLevel={paddingLevel + 1} renderItem={renderItem} renderSection={renderSection} section={childSection} sectionNumber={childSectionNumber}/>);
            })}
      </div>);
        return (<div className={cn(paddingLevel > 0 && 'pl-6')}>
        {renderSection({ section: section, sectionNumber: sectionNumber, paddingLevel: paddingLevel })}
        {displayChildSectionsFirst && customChildSectionsNode}
        {sectionItems.length > 0 && (<div className="mb-4 space-y-4">
            {sectionItems.map(function (item, itemIdx) {
                    var itemNumber = item.itemNumber || (itemIdx + 1).toString();
                    if (renderItem) {
                        return (<div key={item.id}>
                    {renderItem({
                                item: item,
                                itemNumber: itemNumber,
                                sectionId: section.id,
                                sectionNumber: sectionNumber,
                            })}
                  </div>);
                    }
                    return (<AgendaItem key={item.id} headingLevel={itemHeadingLevel} item={item} itemNumber={itemNumber} onAttachmentClick={function (attachment) {
                            onAttachmentClick(__assign(__assign({}, attachment), { agendaSectionItemId: item.agendaSectionItemId, itemNumber: itemNumber, itemTitle: item.shortTitle || item.fullTitle }));
                        }}/>);
                })}
          </div>)}
        {!displayChildSectionsFirst && customChildSectionsNode}
      </div>);
    }
    var childSectionsNode = childSections.length > 0 && (<div className="space-y-6">
      {childSections.map(function (childSection, childIdx) {
            var childSectionNumber = childSection.sectionNumber || "".concat(sectionNumber.replace(/\.$/, ''), ".").concat(childIdx + 1, ".");
            return (<AgendaSectionComponent key={childSection.id} onAttachmentClick={onAttachmentClick} paddingLevel={paddingLevel + 1} renderItem={renderItem} renderSection={renderSection} section={childSection} sectionNumber={childSectionNumber}/>);
        })}
    </div>);
    // Default rendering
    return (<div className={cn('border-primary/60 space-y-2 border-l-4 pl-4', paddingLevel > 0 && 'ml-4')}>
      <SectionHeading className={cn('text-foreground', paddingLevel > 0 && 'text-base', paddingLevel === 0 && 'text-base font-medium')}>
        {sectionNumber} {section.title}
      </SectionHeading>

      {section.description && (<RichTextDisplay className="text-muted-foreground text-sm" content={section.description}/>)}

      {displayChildSectionsFirst && childSectionsNode}

      {sectionItems.length > 0 && (<div className="space-y-2">
          {sectionItems.map(function (item, itemIdx) {
                var itemNumber = item.itemNumber || "".concat(sectionNumber.replace(/\.$/, ''), ".").concat(itemIdx + 1);
                if (renderItem) {
                    return (<div key={item.id}>
                  {renderItem({
                            item: item,
                            itemNumber: itemNumber,
                            sectionId: section.id,
                            sectionNumber: sectionNumber,
                        })}
                </div>);
                }
                return (<AgendaItem key={item.id} headingLevel={itemHeadingLevel} item={item} itemNumber={itemNumber} onAttachmentClick={function (attachment) {
                        onAttachmentClick(__assign(__assign({}, attachment), { agendaSectionItemId: item.agendaSectionItemId, itemNumber: itemNumber, itemTitle: item.shortTitle || item.fullTitle }));
                    }}/>);
            })}
        </div>)}

      {!displayChildSectionsFirst && childSectionsNode}
    </div>);
}
/**
 * AgendaView component for displaying agenda sections with nested children
 * Handles both flat sections (with parentSectionId) and nested sections (with childSections)
 * Includes PDF viewer for attachments with a back button to return to agenda
 */
export function AgendaView(_a) {
    var agenda = _a.agenda, className = _a.className, onAttachmentClick = _a.onAttachmentClick, renderItem = _a.renderItem, renderSection = _a.renderSection, sections = _a.sections;
    var _b = useState(null), viewingAttachment = _b[0], setViewingAttachment = _b[1];
    var handleAttachmentClick = onAttachmentClick !== null && onAttachmentClick !== void 0 ? onAttachmentClick : setViewingAttachment;
    // Organize sections by parent if needed
    var organizedSections = useMemo(function () {
        var sectionsToUse = sections || (agenda === null || agenda === void 0 ? void 0 : agenda.sections) || [];
        // If sections already have childSections, use them as-is
        var hasNestedStructure = sectionsToUse.some(function (s) { return s.childSections && s.childSections.length > 0; });
        if (hasNestedStructure) {
            // Filter to only top-level sections (those without a parent)
            return sectionsToUse.filter(function (section) {
                var _a;
                var sectionInput = section;
                var parentId = (_a = sectionInput.parentId) !== null && _a !== void 0 ? _a : sectionInput.parentSectionId;
                return !parentId;
            });
        }
        // If sections have parentId or parentSectionId, organize them into a tree
        var hasParentStructure = sectionsToUse.some(function (s) {
            var sectionInput = s;
            return sectionInput.parentId != null || sectionInput.parentSectionId != null;
        });
        if (hasParentStructure) {
            var sectionMap_1 = new Map();
            var rootSections_1 = [];
            // First pass: create all sections
            sectionsToUse.forEach(function (section) {
                sectionMap_1.set(section.id, __assign(__assign({}, section), { childSections: [] }));
            });
            // Second pass: organize into tree
            sectionsToUse.forEach(function (section) {
                var _a;
                var sectionWithChildren = sectionMap_1.get(section.id);
                var sectionInput = section;
                var parentId = (_a = sectionInput.parentId) !== null && _a !== void 0 ? _a : sectionInput.parentSectionId;
                if (parentId && sectionMap_1.has(parentId)) {
                    var parent_1 = sectionMap_1.get(parentId);
                    parent_1.childSections = parent_1.childSections || [];
                    parent_1.childSections.push(sectionWithChildren);
                }
                else {
                    rootSections_1.push(sectionWithChildren);
                }
            });
            // Sort by orderById
            var sortSections_1 = function (sections) {
                return __spreadArray([], sections, true).sort(function (a, b) { return a.orderById - b.orderById; });
            };
            var sortRecursive_1 = function (section) {
                if (section.childSections) {
                    section.childSections = sortSections_1(section.childSections);
                    section.childSections.forEach(sortRecursive_1);
                }
            };
            rootSections_1.forEach(sortRecursive_1);
            return sortSections_1(rootSections_1);
        }
        // Default: return sections as-is, sorted by orderById
        return __spreadArray([], sectionsToUse, true).sort(function (a, b) { return a.orderById - b.orderById; });
    }, [agenda === null || agenda === void 0 ? void 0 : agenda.sections, sections]);
    if (organizedSections.length === 0) {
        return null;
    }
    // If viewing an attachment, show PDF viewer
    if (viewingAttachment) {
        return (<div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              {viewingAttachment.itemNumber} - {viewingAttachment.itemTitle}
            </h3>
            <p className="text-muted-foreground text-sm">{viewingAttachment.name}</p>
          </div>
          <Button onClick={function () { return setViewingAttachment(null); }} size="sm" variant="outline">
            <ChevronRight className="mr-1 h-4 w-4 rotate-180"/>
            Back to Agenda
          </Button>
        </div>
        <PdfViewer allowFullscreen className="h-[80vh] rounded-lg border" fallback={<div className="text-muted-foreground flex items-center justify-center py-12">
              Preview is unavailable for this attachment.
            </div>} src={viewingAttachment.signedUrl}/>
      </div>);
    }
    // Show agenda sections
    return (<div className={cn('space-y-6', className)}>
      {organizedSections.map(function (section, sectionIdx) { return (<AgendaSectionComponent key={section.id} onAttachmentClick={handleAttachmentClick} paddingLevel={0} renderItem={renderItem} renderSection={renderSection} section={section} sectionNumber={section.sectionNumber || "".concat(sectionIdx + 1, ".")}/>); })}
    </div>);
}
